/* ==========================================================================
   GLOW POCKET v7 — theme.js
   Stack: Lenis 1.0.42 · GSAP 3.12.2 + ScrollTrigger
   NO Three.js — warm white design system
   Handles: Lenis smooth scroll, GSAP reveals, FAQ/tabs, header, cart,
            mobile nav, before/after slider, BA reveal, ATC, mini-cart
   ========================================================================== */

'use strict';

/* ══════════════════════════════════════════════════════════════════════════
   UTILITY: splitWords
   Hand-rolled word splitter — NEVER use paid GSAP SplitText
   Wraps each word in <span class="sw"><span class="swi">word</span></span>
   Sets aria-label on parent for screen readers
══════════════════════════════════════════════════════════════════════════ */
function splitWords(el) {
  if (!el || el.dataset.split) return;
  const text  = el.textContent.trim();
  const words = text.split(/\s+/);
  el.setAttribute('aria-label', text);
  el.textContent = '';
  words.forEach((word, i) => {
    const sw  = document.createElement('span');
    const swi = document.createElement('span');
    sw.className  = 'sw';
    swi.className = 'swi';
    sw.style.cssText  = 'display:inline-block;overflow:hidden;vertical-align:top;';
    swi.style.cssText = 'display:inline-block;';
    swi.textContent   = word;
    sw.appendChild(swi);
    el.appendChild(sw);
    if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
  });
  el.dataset.split = '1';
}

/* ══════════════════════════════════════════════════════════════════════════
   LENIS SMOOTH SCROLL
══════════════════════════════════════════════════════════════════════════ */
let lenis;

function initLenis() {
  lenis = new Lenis({
    duration:    1.2,
    easing:      t => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    smoothWheel: true,
    touchMultiplier: 1.5,
  });

  // Hook into GSAP ticker for frame-perfect sync
  lenis.on('scroll', ScrollTrigger.update);
  gsap.ticker.add(time => lenis.raf(time * 1000));
  gsap.ticker.lagSmoothing(0);
}

/* ══════════════════════════════════════════════════════════════════════════
   GSAP REVEAL SYSTEM
   Uses IntersectionObserver as primary driver.
   Elements: [data-reveal] get opacity+translateY entrance on viewport entry.
══════════════════════════════════════════════════════════════════════════ */
function initReveal() {
  const els = document.querySelectorAll('[data-reveal]');
  if (!els.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, {
    threshold: 0.12,
    rootMargin: '0px 0px -40px 0px',
  });

  els.forEach(el => observer.observe(el));
}

/* ── GSAP word-reveal for headings ── */
function initWordReveal() {
  document.querySelectorAll('[data-word-reveal]').forEach(el => {
    splitWords(el);
    const words = el.querySelectorAll('.swi');
    gsap.from(words, {
      y: '110%',
      opacity: 0,
      duration: 0.9,
      ease: 'power3.out',
      stagger: 0.06,
      scrollTrigger: {
        trigger: el,
        start: 'top 82%',
        once: true,
      }
    });
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   HEADER
   - Adds .scrolled class on scroll (backdrop blur, border)
   - Adds .hero-mode when page starts over dark hero (homepage only)
   - Offset for announcement bar
══════════════════════════════════════════════════════════════════════════ */
function initHeader() {
  const header       = document.querySelector('.site-header');
  const chrome       = document.getElementById('site-chrome');
  const annBar       = document.querySelector('.announcement-bar');
  const heroSection  = document.querySelector('.hero-section');
  const menuBtn      = document.getElementById('mobile-menu-btn');
  const mobileNav    = document.getElementById('mobile-nav');
  const backdrop     = document.getElementById('mobile-nav-backdrop');
  const closeBtn     = document.getElementById('mobile-nav-close');
  if (!header) return;

  // Announcement bar offset
  if (annBar) {
    const annH = annBar.offsetHeight;
    chrome.style.top = '0px';
    document.body.style.setProperty('--ann-height', annH + 'px');
    header.style.top = annH + 'px';
  }

  // Hero-mode (white text on dark bg)
  const onHomePage = document.body.classList.contains('page-home');
  if (onHomePage && heroSection) {
    header.classList.add('hero-mode');
    // Remove hero-mode once scrolled past hero
    ScrollTrigger.create({
      trigger: heroSection,
      start: 'bottom 90px',
      onEnter:     () => header.classList.remove('hero-mode'),
      onLeaveBack: () => header.classList.add('hero-mode'),
    });
  }

  // Scrolled state
  ScrollTrigger.create({
    start: 'top -8px',
    onEnter:     () => header.classList.add('scrolled'),
    onLeaveBack: () => header.classList.remove('scrolled'),
  });

  // Mobile nav toggle
  function openMobileNav() {
    mobileNav.classList.add('open');
    mobileNav.setAttribute('aria-hidden', 'false');
    backdrop?.classList.add('is-open');
    document.body.classList.add('no-scroll');
    if (lenis) lenis.stop();
  }
  function closeMobileNav() {
    mobileNav.classList.remove('open');
    mobileNav.setAttribute('aria-hidden', 'true');
    backdrop?.classList.remove('is-open');
    document.body.classList.remove('no-scroll');
    if (lenis) lenis.start();
  }

  menuBtn?.addEventListener('click', openMobileNav);
  closeBtn?.addEventListener('click', closeMobileNav);
  backdrop?.addEventListener('click', closeMobileNav);

  // Close on esc
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && mobileNav.classList.contains('open')) closeMobileNav();
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   FAQ ACCORDION
   Both homepage FAQ and product tab accordions use the same data-faq system
══════════════════════════════════════════════════════════════════════════ */
function initFAQ() {
  // Homepage FAQ
  document.querySelectorAll('.faq-trigger').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const item  = trigger.closest('.faq-item');
      const isOpen = item.classList.contains('open');

      // Close all in same list
      const list = item.closest('.faq-list');
      if (list) {
        list.querySelectorAll('.faq-item.open').forEach(openItem => {
          if (openItem !== item) openItem.classList.remove('open');
        });
      }

      item.classList.toggle('open', !isOpen);
      trigger.setAttribute('aria-expanded', (!isOpen).toString());
    });
  });

  // Product page tabs (Description / How to Use / etc.)
  document.querySelectorAll('.pd-tab-trigger').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const item   = trigger.closest('.pd-tab-item');
      const isOpen = item.classList.contains('open');
      // Allow multiple open
      item.classList.toggle('open', !isOpen);
      trigger.setAttribute('aria-expanded', (!isOpen).toString());
    });
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   CART SYSTEM
   - Get cart count on page load
   - Update count badge after AJAX mutations
   - Show toast notification
══════════════════════════════════════════════════════════════════════════ */
const cartCountEls = () => document.querySelectorAll('.header-cart-count');

async function fetchCartCount() {
  try {
    const r = await fetch('/cart.js');
    const data = await r.json();
    updateCartCount(data.item_count);
    return data;
  } catch(e) {
    console.warn('[GP] Cart fetch failed', e);
  }
}

function updateCartCount(count) {
  cartCountEls().forEach(el => {
    el.textContent  = count > 99 ? '99+' : count;
    el.style.display = count > 0 ? 'flex' : 'none';
  });
}

function showCartToast(msg = 'Added to cart') {
  const toast = document.getElementById('cart-toast');
  const msgEl = document.getElementById('cart-toast-msg');
  if (!toast) return;
  if (msgEl) msgEl.textContent = msg;
  toast.classList.add('is-visible');
  clearTimeout(toast._timer);
  toast._timer = setTimeout(() => toast.classList.remove('is-visible'), 3200);
}

/* ATC handler — attached to all .btn-atc and .pd-atc-btn elements */
async function handleATCClick(e) {
  const btn = e.currentTarget;
  if (btn.disabled) return;
  const form = btn.closest('form[action="/cart/add"]') || btn.closest('.pd-form');
  if (!form) return;

  const variantInput = form.querySelector('[name="id"]');
  const qtyInput     = form.querySelector('[name="quantity"]');
  if (!variantInput || !variantInput.value) return;

  const variantId = parseInt(variantInput.value);
  const quantity  = parseInt(qtyInput?.value || '1');

  btn.disabled    = true;
  const origText  = btn.textContent;
  btn.textContent = 'Adding…';

  try {
    const r = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity }),
    });
    if (!r.ok) throw new Error('ATC failed');
    await r.json();

    btn.textContent = '✓ Ajouté au panier';
    showCartToast();
    const cartData = await fetchCartCount();
    if (cartData) window.dispatchEvent(new CustomEvent('gp:atc:success', { detail: { cart: cartData } }));
    setTimeout(() => {
      btn.textContent = origText;
      btn.disabled    = false;
    }, 2000);
  } catch(err) {
    btn.textContent = 'Try again';
    btn.disabled    = false;
    console.warn('[GP] ATC error', err);
    setTimeout(() => { btn.textContent = origText; }, 2500);
  }
}

function initCart() {
  fetchCartCount();

  // Bind ATC clicks (handles dynamic content too)
  document.addEventListener('click', e => {
    const btn = e.target.closest('.js-atc-btn');
    if (btn) handleATCClick({ currentTarget: btn });
  });

  // Cart link — update count on hover
  document.querySelectorAll('a[href="/cart"]').forEach(link => {
    link.addEventListener('mouseenter', fetchCartCount, { once: true });
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   VARIANT SELECTOR (product form)
   Updates hidden <input name="id"> and price display on swatch click
══════════════════════════════════════════════════════════════════════════ */
function initVariantSelector() {
  document.querySelectorAll('.pd-form').forEach(form => {
    const variantInput  = form.querySelector('input[name="id"]');
    const productJSON   = form.querySelector('[data-product-json]');
    if (!variantInput || !productJSON) return;

    let product;
    try { product = JSON.parse(productJSON.textContent); }
    catch(e) { return; }

    const variants = product.variants;
    const options  = {};

    // Build initial option state from active swatches
    form.querySelectorAll('.pd-option').forEach(optGroup => {
      const optName    = optGroup.dataset.option;
      const activeSwatch = optGroup.querySelector('.pd-option-swatch.is-active');
      if (optName && activeSwatch) options[optName] = activeSwatch.dataset.value;
    });

    function findVariant() {
      return variants.find(v =>
        v.options.every((val, i) => {
          const key = product.options[i];
          return !options[key] || options[key] === val;
        })
      );
    }

    function updateForm() {
      const variant = findVariant();
      if (!variant) return;

      variantInput.value = variant.id;

      // Update price
      const priceEl   = form.closest('.pd-info')?.querySelector('.pd-price');
      const compareEl = form.closest('.pd-info')?.querySelector('.pd-compare');
      if (priceEl) {
        const price = (variant.price / 100).toLocaleString('en-US', { style: 'currency', currency: product.currency || 'USD' });
        priceEl.textContent = price;
      }
      if (compareEl && variant.compare_at_price) {
        const cp = (variant.compare_at_price / 100).toLocaleString('en-US', { style: 'currency', currency: product.currency || 'USD' });
        compareEl.textContent = cp;
        compareEl.style.display = '';
      } else if (compareEl) {
        compareEl.style.display = 'none';
      }

      // ATC availability
      const atcBtn = form.querySelector('.js-atc-btn');
      if (atcBtn) {
        atcBtn.disabled    = !variant.available;
        atcBtn.textContent = variant.available ? atcBtn.dataset.label || 'Add to Cart' : 'Sold Out';
      }

      // Update sticky ATC
      const satcVariant = document.querySelector('.satc-variant-select');
      if (satcVariant) satcVariant.value = variant.id;

      // FIX B: Switch gallery to variant image
      if (variant.featured_image && typeof window.pdGalleryGoToImageId === 'function') {
        window.pdGalleryGoToImageId(variant.featured_image.id);
      }
    }

    // Swatch clicks
    form.addEventListener('click', e => {
      const swatch = e.target.closest('.pd-option-swatch');
      if (!swatch) return;

      const optGroup = swatch.closest('.pd-option');
      const optName  = optGroup?.dataset.option;
      if (!optName) return;

      // Deactivate siblings
      optGroup.querySelectorAll('.pd-option-swatch').forEach(s => s.classList.remove('is-active'));
      swatch.classList.add('is-active');
      options[optName] = swatch.dataset.value;
      updateForm();
    });

    // Qty controls
    const qtyInput = form.querySelector('.pd-qty-input');
    form.querySelectorAll('.pd-qty-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const delta = parseInt(btn.dataset.delta || '0');
        const curr  = parseInt(qtyInput?.value || '1');
        const next  = Math.max(1, curr + delta);
        if (qtyInput) qtyInput.value = next;
      });
    });

    updateForm();
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   STICKY ATC (mobile — appears when main ATC scrolls out of view)
══════════════════════════════════════════════════════════════════════════ */
function initStickyATC() {
  const satc    = document.querySelector('.satc');
  const mainATC = document.querySelector('.pd-atc-btn');
  if (!satc || !mainATC) return;

  const observer = new IntersectionObserver(entries => {
    const entry = entries[0];
    satc.classList.toggle('is-visible', !entry.isIntersecting);
  }, { threshold: 0 });

  observer.observe(mainATC);

  // Sync variant to sticky ATC
  const satcAtcBtn = satc.querySelector('.satc-atc-btn');
  const satcVariantSelect = satc.querySelector('.satc-variant-select');
  if (satcAtcBtn) {
    satcAtcBtn.addEventListener('click', () => {
      const variantId = satcVariantSelect?.value || document.querySelector('input[name="id"]')?.value;
      if (!variantId) return;
      handleATCClick({ currentTarget: satcAtcBtn });
    });
  }
}

/* ══════════════════════════════════════════════════════════════════════════
   BEFORE / AFTER SLIDER
   Drag-based reveal slider
══════════════════════════════════════════════════════════════════════════ */
function initBASlider() {
  document.querySelectorAll('.ba-slider').forEach(slider => {
    const beforeEl = slider.querySelector('.ba-before');
    const afterEl  = slider.querySelector('.ba-after');
    const handle   = slider.querySelector('.ba-handle');
    if (!beforeEl || !afterEl || !handle) return;

    let isDragging = false;

    /* pos = 0..1 where 0 = fully before, 1 = fully after */
    function setPosition(clientX) {
      const rect = slider.getBoundingClientRect();
      const pos  = Math.min(Math.max((clientX - rect.left) / rect.width, 0.03), 0.97);
      /* Clip before-image from the right so after-image shows on the right of handle */
      beforeEl.style.clipPath = `inset(0 ${(1 - pos) * 100}% 0 0)`;
      handle.style.left       = `${pos * 100}%`;
    }

    /* Mouse */
    slider.addEventListener('mousedown', e => {
      isDragging = true;
      setPosition(e.clientX);
    });
    window.addEventListener('mouseup', () => { isDragging = false; });
    window.addEventListener('mousemove', e => {
      if (isDragging) setPosition(e.clientX);
    });

    /* Touch — passive:false lets us preventDefault to block page scroll while dragging */
    slider.addEventListener('touchstart', e => {
      isDragging = true;
      e.preventDefault();           /* stop Lenis / native scroll from competing */
      setPosition(e.touches[0].clientX);
    }, { passive: false });

    window.addEventListener('touchend', () => { isDragging = false; });

    /* Add/remove the touchmove listener dynamically so passive:false only applies during drag */
    const onTouchMove = e => {
      if (!isDragging) return;
      e.preventDefault();
      setPosition(e.touches[0].clientX);
    };
    slider.addEventListener('touchstart', () => {
      window.addEventListener('touchmove', onTouchMove, { passive: false });
    }, { passive: true });
    window.addEventListener('touchend', () => {
      window.removeEventListener('touchmove', onTouchMove);
    });

    /* Initialise at 50% */
    const initX = slider.getBoundingClientRect().left + slider.offsetWidth * 0.5;
    setPosition(initX);
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   COUNTER ANIMATIONS
   Elements: .counter-num[data-value][data-suffix]
══════════════════════════════════════════════════════════════════════════ */
function initCounters() {
  document.querySelectorAll('.counter-num').forEach(el => {
    const target   = parseFloat(el.dataset.value || '0');
    const suffix   = el.dataset.suffix || '';
    const decimals = parseInt(el.dataset.decimals || '0');

    gsap.from(el, {
      textContent: 0,
      duration:    2,
      ease:        'power1.out',
      snap:        { textContent: decimals > 0 ? 0.1 : 1 },
      onUpdate: function() {
        const v = parseFloat(this.targets()[0].textContent);
        this.targets()[0].textContent = decimals > 0
          ? v.toFixed(decimals) + suffix
          : Math.round(v) + suffix;
      },
      scrollTrigger: {
        trigger:     el,
        start:       'top 80%',
        toggleActions: 'play none none none',
      }
    });
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   SOCIAL TICKER / ANNOUNCEMENT TICKER
   Auto-duplicates for seamless loop
══════════════════════════════════════════════════════════════════════════ */
function initTickers() {
  document.querySelectorAll('.announcement-ticker, .social-ticker__track').forEach(ticker => {
    // Clone for seamless loop
    const clone = ticker.innerHTML;
    ticker.innerHTML = clone + clone;
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   PRODUCT GALLERY
   Thumbnail clicks update main image
══════════════════════════════════════════════════════════════════════════ */
function initGallery() {
  document.querySelectorAll('.pd-gallery').forEach(gallery => {
    const mainImg = gallery.querySelector('.pd-gallery-main');
    const thumbs  = gallery.querySelectorAll('.pd-thumb');
    if (!mainImg || !thumbs.length) return;

    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const src = thumb.dataset.src || thumb.querySelector('img')?.src;
        if (src && mainImg) mainImg.src = src;

        thumbs.forEach(t => t.classList.remove('is-active'));
        thumb.classList.add('is-active');
      });
    });
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   EMAIL POPUP (Klaviyo)
   Shows popup after 4s delay (or on exit intent) unless dismissed/subscribed
══════════════════════════════════════════════════════════════════════════ */
function initEmailPopup() {
  const popup    = document.getElementById('email-popup');
  const backdrop = document.getElementById('popup-backdrop');
  const closeBtn = document.getElementById('popup-close');
  const form     = document.getElementById('popup-form');
  const copyBtn  = document.getElementById('popup-copy-btn');
  if (!popup) return;

  const DISMISSED_KEY = 'gp_popup_dismissed';
  const SHOWN_KEY     = 'gp_popup_shown';

  function isBot() {
    return /bot|crawl|spider|scrape/i.test(navigator.userAgent);
  }

  function shouldShow() {
    if (isBot()) return false;
    if (localStorage.getItem(DISMISSED_KEY)) return false;
    if (sessionStorage.getItem(SHOWN_KEY)) return false;
    return true;
  }

  function openPopup() {
    popup.classList.add('is-open');
    backdrop?.classList.add('is-open');
    sessionStorage.setItem(SHOWN_KEY, '1');
    document.body.classList.add('no-scroll');
    if (lenis) lenis.stop();
  }

  function closePopup() {
    popup.classList.remove('is-open');
    backdrop?.classList.remove('is-open');
    document.body.classList.remove('no-scroll');
    if (lenis) lenis.start();
    localStorage.setItem(DISMISSED_KEY, Date.now());
  }

  if (!shouldShow()) return;

  // Delay trigger
  setTimeout(() => {
    openPopup();
  }, 4000);

  // Exit intent (desktop)
  document.addEventListener('mouseleave', e => {
    if (e.clientY < 0 && !popup.classList.contains('is-open') && shouldShow()) {
      openPopup();
    }
  });

  closeBtn?.addEventListener('click', closePopup);
  backdrop?.addEventListener('click', closePopup);
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && popup.classList.contains('is-open')) closePopup();
  });

  // Dismiss link
  document.querySelectorAll('.popup-dismiss').forEach(el => {
    el.addEventListener('click', e => {
      e.preventDefault();
      closePopup();
    });
  });

  // Copy code
  copyBtn?.addEventListener('click', () => {
    const code = copyBtn.dataset.code || '';
    navigator.clipboard?.writeText(code).then(() => {
      copyBtn.textContent = 'Copied!';
      setTimeout(() => { copyBtn.textContent = 'Copy'; }, 2000);
    });
  });

  // Klaviyo subscribe
  form?.addEventListener('submit', async e => {
    e.preventDefault();
    const emailInput = form.querySelector('input[type="email"]');
    const email      = emailInput?.value?.trim();
    const listId     = form.dataset.klaviyoList || '';
    if (!email || !listId) return;

    const submitBtn = form.querySelector('button[type="submit"]');
    if (submitBtn) submitBtn.textContent = 'Subscribing…';

    try {
      await fetch('https://manage.kmail-lists.com/ajax/subscriptions/subscribe', {
        method: 'POST',
        headers: { 'content-type': 'application/x-www-form-urlencoded' },
        body: `g=${encodeURIComponent(listId)}&email=${encodeURIComponent(email)}`,
      });

      // Show success state
      const success = form.querySelector('.popup-success');
      form.style.display = 'none';
      if (success) success.style.display = 'block';
      localStorage.setItem(DISMISSED_KEY, Date.now());
    } catch(err) {
      if (submitBtn) submitBtn.textContent = 'Try again';
    }
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   COLLECTION PAGE: Filter panel + grid view toggle
══════════════════════════════════════════════════════════════════════════ */
function initCollection() {
  const filterBtn  = document.querySelector('.collection-filter-btn');
  const filtersPanel = document.querySelector('.collection-filters');
  const gridViewBtns = document.querySelectorAll('.grid-view-btn');
  const grid       = document.querySelector('.products-grid');

  filterBtn?.addEventListener('click', () => {
    filtersPanel?.classList.toggle('is-open');
  });

  gridViewBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      gridViewBtns.forEach(b => b.classList.remove('is-active'));
      btn.classList.add('is-active');
      const cols = btn.dataset.cols || '3';
      if (grid) {
        grid.className = grid.className.replace(/products-grid--\dcol/g, '');
        grid.classList.add(`products-grid--${cols}col`);
      }
    });
  });

  // Sort
  const sortSelect = document.querySelector('.collection-sort');
  sortSelect?.addEventListener('change', () => {
    const url = new URL(window.location);
    url.searchParams.set('sort_by', sortSelect.value);
    window.location = url.toString();
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   REVIEW FORM — star rating
══════════════════════════════════════════════════════════════════════════ */
function initReviewForm() {
  document.querySelectorAll('.pr-form-stars').forEach(starsRow => {
    const stars = starsRow.querySelectorAll('.pr-form-star');
    let selected = 0;

    stars.forEach((star, i) => {
      star.addEventListener('mouseenter', () => {
        stars.forEach((s, j) => { s.style.color = j <= i ? 'var(--accent)' : 'var(--border)'; });
      });
      star.addEventListener('mouseleave', () => {
        stars.forEach((s, j) => { s.style.color = j < selected ? 'var(--accent)' : 'var(--border)'; });
      });
      star.addEventListener('click', () => {
        selected = i + 1;
        const ratingInput = starsRow.closest('.pr-form')?.querySelector('input[name="review[rating]"]');
        if (ratingInput) ratingInput.value = selected;
      });
    });
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   CONTACT FORM — basic validation
══════════════════════════════════════════════════════════════════════════ */
function initContactForm() {
  const form = document.querySelector('.contact-form');
  if (!form) return;
  // Shopify handles the submission natively — just add a loading state
  form.addEventListener('submit', () => {
    const btn = form.querySelector('button[type="submit"]');
    if (btn) btn.textContent = 'Sending…';
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   SCROLL PROGRESS BAR (cosmetic, top of page)
══════════════════════════════════════════════════════════════════════════ */
function initScrollProgress() {
  const bar = document.getElementById('scroll-progress');
  if (!bar) return;

  gsap.to(bar, {
    scaleX: 1,
    ease: 'none',
    transformOrigin: 'left center',
    scrollTrigger: {
      scrub: 0.3,
      start: 'top top',
      end:   'bottom bottom',
    }
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   GSAP SECTION ENTRANCE (staggered child reveals)
   Used for trust grid, product grid, etc.
══════════════════════════════════════════════════════════════════════════ */
function initSectionAnimations() {
  // Trust badges stagger
  document.querySelectorAll('.trust-grid, .testi-grid, .products-grid').forEach(grid => {
    const cards = grid.children;
    gsap.from(cards, {
      y: 40,
      opacity: 0,
      stagger: 0.08,
      duration: 0.7,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: grid,
        start:   'top 80%',
        once:    true,
      }
    });
  });

  // Stats counter sections
  document.querySelectorAll('.trust-stat-number, .ba-stat-number').forEach(el => {
    const numEl = el.querySelector('.counter-num') || el;
    // Handled by initCounters
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   SOCIAL TICKER pause on hover
══════════════════════════════════════════════════════════════════════════ */
function initTickerHover() {
  document.querySelectorAll('.social-ticker, .announcement-ticker').forEach(ticker => {
    ticker.addEventListener('mouseenter', () => ticker.style.animationPlayState = 'paused');
    ticker.addEventListener('mouseleave', () => ticker.style.animationPlayState = '');
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   CART PAGE
   Qty controls + remove items via AJAX
══════════════════════════════════════════════════════════════════════════ */
function initCartPage() {
  const cartForm = document.querySelector('.cart-page form');
  if (!cartForm) return;

  async function updateLine(line, quantity) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line, quantity }),
    });
    window.location.reload();
  }

  cartForm.addEventListener('click', async e => {
    const qtyBtn   = e.target.closest('.cart-qty-btn');
    const rmBtn    = e.target.closest('.cart-remove-btn');

    if (qtyBtn) {
      e.preventDefault();
      const row    = qtyBtn.closest('.cart-item');
      const lineIndex = parseInt(row?.dataset.line || '1');
      const qtyEl  = row?.querySelector('.cart-qty-value');
      const curr   = parseInt(qtyEl?.value || qtyEl?.textContent || '1');
      const delta  = parseInt(qtyBtn.dataset.delta || '0');
      await updateLine(lineIndex, Math.max(0, curr + delta));
    }

    if (rmBtn) {
      e.preventDefault();
      const row       = rmBtn.closest('.cart-item');
      const lineIndex = parseInt(row?.dataset.line || '1');
      await updateLine(lineIndex, 0);
    }
  });

  // Promo code
  const promoForm = document.querySelector('.cart-promo-form');
  promoForm?.addEventListener('submit', e => {
    e.preventDefault();
    const code = promoForm.querySelector('.cart-promo-input')?.value;
    if (code) {
      window.location = `/discount/${code}?redirect=/cart`;
    }
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   BOOT
══════════════════════════════════════════════════════════════════════════ */
document.addEventListener('DOMContentLoaded', () => {
  if (typeof gsap === 'undefined') return;

  gsap.registerPlugin(ScrollTrigger);

  initLenis();
  initHeader();
  initReveal();
  initWordReveal();
  initFAQ();
  initCart();
  initVariantSelector();
  initStickyATC();
  initBASlider();
  initCounters();
  initTickers();
  initGallery();
  initEmailPopup();
  initCollection();
  initReviewForm();
  initContactForm();
  initScrollProgress();
  initSectionAnimations();
  initTickerHover();
  initCartPage();

  ScrollTrigger.refresh();
});
